<template>
  <a-modal :open="isVisible" :title="isEditMode ? '클래스 수정' : '새 클래스 생성'" size="md" @close="closeModal">
    <form id="class-create-form" class="p-form" @submit.prevent="handleSubmit">
      <div class="p-field">
        <label class="p-field__label">클래스 이름 <span class="p-field__req">*</span></label>
        <input
          v-model="formData.name"
          type="text"
          class="p-input"
          :class="{ 'is-invalid': errors.name }"
          placeholder="클래스 이름을 입력하세요"
          maxlength="50"
          required
        />
        <div v-if="errors.name" class="p-field__err">{{ errors.name }}</div>
        <div v-else class="p-field__hint">최대 50자</div>
      </div>

      <div class="p-field">
        <label class="p-field__label">클래스 설명</label>
        <textarea
          v-model="formData.description"
          class="p-input p-input--area"
          :class="{ 'is-invalid': errors.description }"
          placeholder="클래스에 대한 상세 설명을 입력하세요"
          rows="3"
          maxlength="500"
        />
        <div v-if="errors.description" class="p-field__err">{{ errors.description }}</div>
        <div v-else class="p-field__hint">최대 500자 ({{ formData.description.length }}/500)</div>
      </div>

      <div class="p-form__row">
        <div class="p-field">
          <label class="p-field__label">시작일 <span class="p-field__req">*</span></label>
          <input
            v-model="formData.start_date"
            type="date"
            class="p-input"
            :class="{ 'is-invalid': errors.start_date }"
            required
          />
          <div v-if="errors.start_date" class="p-field__err">{{ errors.start_date }}</div>
        </div>
        <div class="p-field">
          <label class="p-field__label">종료일 <span class="p-field__req">*</span></label>
          <input
            v-model="formData.end_date"
            type="date"
            class="p-input"
            :class="{ 'is-invalid': errors.end_date }"
            :min="formData.start_date"
            required
          />
          <div v-if="errors.end_date" class="p-field__err">{{ errors.end_date }}</div>
        </div>
      </div>

      <div class="p-field">
        <label class="p-field__label">클래스 상태</label>
        <select v-model="formData.class_status" class="p-input p-select">
          <option value="ongoing">진행중</option>
          <option value="closed">종료</option>
          <option value="cancelled">취소</option>
        </select>
        <div class="p-field__hint">기본값: 진행중</div>
      </div>

      <div v-if="formData.start_date && formData.end_date" class="p-alert p-alert--info">
        <div class="p-alert__title">수강 기간 안내</div>
        <div class="p-alert__body">총 <strong>{{ calculateDuration }}</strong>일</div>
      </div>
    </form>

    <template #foot>
      <a-btn kind="ghost" type="button" :disabled="isSubmitting" @click="closeModal">취소</a-btn>
      <a-btn kind="primary" type="submit" form="class-create-form" :disabled="isSubmitting || !isFormValid">
        <template v-if="isSubmitting">{{ isEditMode ? '수정 중...' : '생성 중...' }}</template>
        <template v-else>{{ isEditMode ? '클래스 수정' : '클래스 생성' }}</template>
      </a-btn>
    </template>
  </a-modal>
</template>

<script>
import AModal from '@/components/Pineapple/AModal.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';

export default {
  name: 'ClassCreateModal',
  components: { AModal, ABtn },
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    editData: {
      type: Object,
      default: null
    }
  },
  emits: ['close', 'created', 'updated'],
  data() {
    return {
      formData: {
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        class_status: 'ongoing'
      },
      errors: {},
      isSubmitting: false
    }
  },
  computed: {
    isEditMode() {
      return this.editData !== null
    },
    isFormValid() {
      return (
        this.formData.name.trim() !== '' &&
        this.formData.start_date !== '' &&
        this.formData.end_date !== '' &&
        this.formData.end_date >= this.formData.start_date
      )
    },
    calculateDuration() {
      if (!this.formData.start_date || !this.formData.end_date) return 0
      const start = new Date(this.formData.start_date)
      const end = new Date(this.formData.end_date)
      const diffTime = Math.abs(end - start)
      return Math.ceil(diffTime / (1000 * 60 * 60 * 24))
    }
  },
  watch: {
    isVisible(newVal) {
      if (newVal) {
        if (this.editData) {
          this.loadEditData()
        } else {
          this.resetForm()
        }
      }
    }
  },
  methods: {
    resetForm() {
      this.formData = {
        name: '',
        description: '',
        start_date: '',
        end_date: '',
        class_status: 'ongoing'
      }
      this.errors = {}
      this.isSubmitting = false
    },
    loadEditData() {
      // 날짜 형식 변환 (ISO 8601 -> YYYY-MM-DD)
      const formatDate = (dateStr) => {
        if (!dateStr) return ''
        return dateStr.split('T')[0]
      }

      this.formData = {
        name: this.editData.class_name || this.editData.name || '',
        description: this.editData.description || '',
        start_date: formatDate(this.editData.start_date),
        end_date: formatDate(this.editData.end_date),
        class_status: this.editData.class_status || 'ongoing'
      }
      this.errors = {}
      this.isSubmitting = false
    },
    closeModal() {
      this.$emit('close')
    },
    validateForm() {
      this.errors = {}

      // 클래스 이름 검증
      if (!this.formData.name.trim()) {
        this.errors.name = '클래스 이름은 필수입니다.'
      } else if (this.formData.name.length > 50) {
        this.errors.name = '클래스 이름은 50자를 초과할 수 없습니다.'
      }

      // 설명 검증
      if (this.formData.description.length > 500) {
        this.errors.description = '설명은 500자를 초과할 수 없습니다.'
      }

      // 시작일 검증
      if (!this.formData.start_date) {
        this.errors.start_date = '시작일은 필수입니다.'
      }

      // 종료일 검증
      if (!this.formData.end_date) {
        this.errors.end_date = '종료일은 필수입니다.'
      } else if (this.formData.end_date < this.formData.start_date) {
        this.errors.end_date = '종료일은 시작일보다 이후여야 합니다.'
      }

      return Object.keys(this.errors).length === 0
    },
    async handleSubmit() {
      if (!this.validateForm()) {
        return
      }

      this.isSubmitting = true

      try {
        // API 요청 데이터 준비 (PRD 문서 형식에 맞춤)
        const requestData = {
          name: this.formData.name.trim(),
          description: this.formData.description.trim() || null,
          start_date: this.formData.start_date,
          end_date: this.formData.end_date,
          class_status: this.formData.class_status
        }

        let response
        if (this.isEditMode) {
          // 편집 모드: PUT 요청
          response = await this.$axios({
            method: 'put',
            url: `/learning/classrooms/${this.editData.idx}`,
            data: requestData,
            loadingBar: true
          })
        } else {
          // 생성 모드: POST 요청
          response = await this.$axios({
            method: 'post',
            url: '/learning/classrooms',
            data: requestData,
            loadingBar: true
          })
        }

        if (response.status === 200) {
          this.$notifyVue({
            type: 'success',
            title: this.isEditMode ? '클래스가 성공적으로 수정되었습니다.' : '클래스가 성공적으로 생성되었습니다.'
          })
          if (this.isEditMode) {
            this.$emit('updated', response.data)
          } else {
            this.$emit('created', response.data)
          }
          this.closeModal()
        }
      } catch (error) {
        console.error(this.isEditMode ? '클래스 수정 오류:' : '클래스 생성 오류:', error)

        // 에러 응답 처리
        if (error.response) {
          const status = error.response.status
          const detail = error.response.data?.detail

          switch (status) {
            case 403:
              this.$notifyVue({
                type: 'error',
                title: detail || '강사만 클래스 등록이 가능합니다.'
              })
              break
            case 400:
              this.$notifyVue({
                type: 'error',
                title: detail || '강사 정보를 찾을 수 없습니다.'
              })
              break
            case 422:
              this.$notifyVue({
                type: 'error',
                title: '입력 데이터를 확인해주세요.'
              })
              // 필드별 에러 처리
              if (Array.isArray(detail)) {
                detail.forEach(err => {
                  const field = err.loc?.[1]
                  if (field && Object.prototype.hasOwnProperty.call(this.errors, field)) {
                    this.errors[field] = err.msg
                  }
                })
              }
              break
            case 500:
              this.$notifyVue({
                type: 'error',
                title: '서버 오류가 발생했습니다. 관리자에게 문의하세요.'
              })
              break
            default:
              this.$notifyVue({
                type: 'error',
                title: detail || (this.isEditMode ? '클래스 수정에 실패했습니다.' : '클래스 생성에 실패했습니다.')
              })
          }
        } else {
          this.$notifyVue({
            type: 'error',
            title: '네트워크 오류가 발생했습니다.'
          })
        }
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>
